import { MarkdownParser } from '../utils/jsmind.markdown';
import { FlashMapNode, NodeImageMeta } from '../models/types';
import {
  NodeRenderer,
  NodeRenderContext,
  NodeRenderResult,
  continueChain,
  stopChain,
} from './node-renderer';

const DEFAULT_IMAGE_SIZE_PX = 96;
const MIN_IMAGE_SIZE_PX = 32;
const MAX_IMAGE_SIZE_PX = 640;
const KEYBOARD_RESIZE_STEP_PX = 8;
const KEYBOARD_RESIZE_LARGE_STEP_PX = 32;

const imageRenderCache = new WeakMap<HTMLElement, string>();

type NodeImageSize = {
  width: number;
  height: number;
};

type JsMindImageLoadHost = {
  get_editable?: () => boolean;
  _refresh_node_view?: (node: FlashMapNode) => void;
  layout?: { layout?: () => void };
  view?: {
    refresh_node_geometry?: (node: FlashMapNode) => void;
    zoom_current?: number;
    relayout?: () => void;
    update_node?: (node: FlashMapNode) => void;
  };
};

type ImageResizeCommit = (nodeId: string, image: NodeImageMeta) => void;

export class ImageNodeRenderer implements NodeRenderer {
  constructor(private readonly commitImageResize?: ImageResizeCommit) {}

  render(context: NodeRenderContext): NodeRenderResult {
    const { jm, ele, node, options } = context;
    if (!(ele instanceof HTMLElement)) {
      return continueChain;
    }
    const nodeImage = this.getNodeImage(node);
    if (!isNodeImageMeta(nodeImage)) {
      imageRenderCache.delete(ele);
      ele.classList.remove('has-node-image');
      return continueChain;
    }
    const position = nodeImage.position;
    const normalizedPosition =
      position === 'top' || position === 'bottom' || position === 'left' || position === 'right'
        ? position
        : 'left';
    const imageSize = normalizeImageSize(nodeImage);
    const editable = this.canResizeImage(jm);
    const renderKey = `${options.support_html ? '1' : '0'}|${node.topic}|${nodeImage.src}|${normalizedPosition}|${imageSize.width}|${imageSize.height}|${editable ? '1' : '0'}`;
    const existingIndicators = ele.querySelector('.flashcard-indicators') as HTMLElement | null;
    const existingBadge = !existingIndicators
      ? (ele.querySelector('.flashcard-indicator') as HTMLElement | null)
      : null;
    const existingMetrics = ele.querySelector('.jmnode-metrics') as HTMLElement | null;
    if (imageRenderCache.get(ele) === renderKey && ele.querySelector('.jmnode-content')) {
      return stopChain;
    }

    const htmlContent = options.support_html
      ? MarkdownParser.parse(node.topic)
      : MarkdownParser.toPlainTextHtml(node.topic);
    // Preserve existing indicators and badges before clearing

    const wrapper = document.createElement('div');
    wrapper.className = `jmnode-content jmnode-content--${normalizedPosition}`;

    const text = document.createElement('div');
    text.className = 'jmnode-text';
    text.innerHTML = htmlContent;

    const imageMenuButton = document.createElement('button');
    imageMenuButton.type = 'button';
    imageMenuButton.className = 'jmnode-image-menu';
    imageMenuButton.setAttribute('aria-label', 'Image options');
    imageMenuButton.setAttribute('title', 'Image options');
    const imageMenuIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    imageMenuIcon.setAttribute('class', 'sa-icon sa-bold sa-nofill');
    imageMenuIcon.setAttribute('aria-hidden', 'true');

    const imageMenuIconUse = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    imageMenuIconUse.setAttribute('href', '/assets/icons/sprite.svg#image');
    imageMenuIcon.appendChild(imageMenuIconUse);
    imageMenuButton.appendChild(imageMenuIcon);
    imageMenuButton.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      ele.dispatchEvent(
        new CustomEvent('flash-map-image-menu', {
          bubbles: true,
          composed: true,
          detail: { nodeId: node.id },
        }),
      );
    });
    text.appendChild(imageMenuButton);

    const imageFrame = document.createElement('span');
    imageFrame.className = 'jmnode-image-frame';
    this.applyImageSize(imageFrame, imageSize);

    const image = document.createElement('img');
    image.className = 'jmnode-image';
    image.alt = '';
    this.applyImageSize(image, imageSize);
    let resizeHandle: HTMLElement | null = null;
    image.onload = () => {
      if (resizeHandle) {
        this.updateResizeHandlePosition(resizeHandle, image, imageSize);
      }
      this.refreshLayoutAfterImageLoad(jm, node);
    };
    imageFrame.appendChild(image);

    if (editable) {
      resizeHandle = this.createResizeHandle(jm, node, nodeImage, imageFrame, image, imageSize);
      imageFrame.appendChild(resizeHandle);
    }
    image.src = nodeImage.src;
    if (resizeHandle && image.complete) {
      this.updateResizeHandlePosition(resizeHandle, image, imageSize);
    }

    if (normalizedPosition === 'bottom' || normalizedPosition === 'right') {
      wrapper.appendChild(text);
      wrapper.appendChild(imageFrame);
    } else {
      wrapper.appendChild(imageFrame);
      wrapper.appendChild(text);
    }

    ele.innerHTML = '';
    ele.classList.add('has-node-image');
    ele.appendChild(wrapper);
    imageRenderCache.set(ele, renderKey);

    // Restore indicators if they existed
    if (existingMetrics) {
      ele.appendChild(existingMetrics);
    }
    if (existingIndicators) {
      ele.appendChild(existingIndicators);
    } else if (existingBadge) {
      ele.appendChild(existingBadge);
    }

    return stopChain;
  }

  private createResizeHandle(
    jm: unknown,
    node: FlashMapNode,
    nodeImage: NodeImageMeta,
    imageFrame: HTMLElement,
    image: HTMLImageElement,
    initialSize: NodeImageSize,
  ): HTMLElement {
    const handle = document.createElement('span');
    handle.className = 'jmnode-image-resize-handle';
    handle.tabIndex = 0;
    handle.setAttribute('role', 'slider');
    handle.setAttribute('aria-label', 'Resize image');
    handle.setAttribute('aria-valuemin', String(MIN_IMAGE_SIZE_PX));
    handle.setAttribute('aria-valuemax', String(MAX_IMAGE_SIZE_PX));
    this.updateResizeHandleValue(handle, initialSize);

    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || !this.canResizeImage(jm)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      const zoom = this.getZoom(jm);
      let pendingSize = initialSize;
      let renderedSize = initialSize;
      let animationFrameId: number | null = null;
      let active = true;

      const renderPendingSize = (): void => {
        animationFrameId = null;
        renderedSize = pendingSize;
        this.applyImageSize(imageFrame, renderedSize);
        this.applyImageSize(image, renderedSize);
        this.updateResizeHandleValue(handle, renderedSize);
        this.updateResizeHandlePosition(handle, image, renderedSize);
        this.refreshLayoutDuringImageResize(jm, node);
      };

      const scheduleRender = (): void => {
        if (animationFrameId !== null) {
          return;
        }
        if (typeof globalThis.requestAnimationFrame === 'function') {
          animationFrameId = globalThis.requestAnimationFrame(renderPendingSize);
        } else {
          renderPendingSize();
        }
      };

      const resize = (moveEvent: PointerEvent): void => {
        if (!active || moveEvent.pointerId !== pointerId) {
          return;
        }
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        pendingSize = resizeProportionally(
          initialSize,
          (moveEvent.clientX - startX) / zoom,
          (moveEvent.clientY - startY) / zoom,
        );
        scheduleRender();
      };

      const finish = (finishEvent: PointerEvent): void => {
        if (!active || finishEvent.pointerId !== pointerId) {
          return;
        }
        finishEvent.preventDefault();
        finishEvent.stopPropagation();
        active = false;

        handle.removeEventListener('pointermove', resize);
        handle.removeEventListener('pointerup', finish);
        handle.removeEventListener('pointercancel', finish);
        if (animationFrameId !== null) {
          globalThis.cancelAnimationFrame?.(animationFrameId);
          renderPendingSize();
        }
        if (handle.hasPointerCapture?.(pointerId)) {
          handle.releasePointerCapture(pointerId);
        }

        if (
          renderedSize.width !== initialSize.width ||
          renderedSize.height !== initialSize.height
        ) {
          this.commitImageResize?.(node.id, {
            ...nodeImage,
            width: renderedSize.width,
            height: renderedSize.height,
          });
        }
      };

      handle.addEventListener('pointermove', resize);
      handle.addEventListener('pointerup', finish);
      handle.addEventListener('pointercancel', finish);
      handle.setPointerCapture?.(pointerId);
    });

    handle.addEventListener('keydown', (event) => {
      const direction = keyboardResizeDirection(event.key);
      if (direction === 0 || !this.canResizeImage(jm)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      const currentSize = normalizeImageSize(this.getNodeImage(node) ?? nodeImage);
      const step = event.shiftKey ? KEYBOARD_RESIZE_LARGE_STEP_PX : KEYBOARD_RESIZE_STEP_PX;
      const resized = resizeFromKeyboard(currentSize, direction * step);
      if (resized.width === currentSize.width && resized.height === currentSize.height) {
        return;
      }
      this.commitImageResize?.(node.id, {
        ...nodeImage,
        width: resized.width,
        height: resized.height,
      });
    });

    return handle;
  }

  private canResizeImage(jm: unknown): boolean {
    const mindMap = jm as JsMindImageLoadHost | null;
    return Boolean(this.commitImageResize && mindMap?.get_editable?.());
  }

  private getZoom(jm: unknown): number {
    const mindMap = jm as JsMindImageLoadHost | null;
    const zoom = Number(mindMap?.view?.zoom_current);
    return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  }

  private applyImageSize(element: HTMLElement, size: NodeImageSize): void {
    element.style.width = `${size.width}px`;
    element.style.height = `${size.height}px`;
  }

  private updateResizeHandleValue(handle: HTMLElement, size: NodeImageSize): void {
    handle.setAttribute('aria-valuenow', String(size.width));
    handle.setAttribute('aria-valuetext', `${size.width} by ${size.height} pixels`);
  }

  private updateResizeHandlePosition(
    handle: HTMLElement,
    image: HTMLImageElement,
    frameSize: NodeImageSize,
  ): void {
    const insets = calculateContainedImageInsets(frameSize, {
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    handle.style.right = `${insets.right}px`;
    handle.style.bottom = `${insets.bottom}px`;
  }

  private refreshLayoutDuringImageResize(jm: unknown, node: FlashMapNode): void {
    const mindMap = jm as JsMindImageLoadHost | null;
    mindMap?.view?.refresh_node_geometry?.(node);
  }

  private refreshLayoutAfterImageLoad(jm: unknown, node: FlashMapNode): void {
    const mindMap = jm as JsMindImageLoadHost | null;
    if (!mindMap) {
      return;
    }

    if (typeof mindMap._refresh_node_view === 'function') {
      mindMap._refresh_node_view(node);
      return;
    }

    mindMap.view?.update_node?.(node);
    mindMap.layout?.layout?.();
    mindMap.view?.relayout?.();
  }

  private getNodeImage(node: FlashMapNode): NodeImageMeta | null {
    const nodeData = node.data ?? {};
    const nestedData = nodeData.data ?? null;
    const nodeImage = nodeData.image ?? nestedData?.image ?? null;
    return nodeImage;
  }
}

const isNodeImageMeta = (value: unknown): value is NodeImageMeta => {
  return Boolean(
    value && typeof value === 'object' && typeof (value as NodeImageMeta).src === 'string',
  );
};

const normalizeImageSize = (image: Pick<NodeImageMeta, 'width' | 'height'>): NodeImageSize => ({
  width: normalizeImageDimension(image.width),
  height: normalizeImageDimension(image.height),
});

const normalizeImageDimension = (value: unknown): number => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return DEFAULT_IMAGE_SIZE_PX;
  }
  return clamp(Math.round(numericValue), MIN_IMAGE_SIZE_PX, MAX_IMAGE_SIZE_PX);
};

const resizeProportionally = (
  initialSize: NodeImageSize,
  deltaX: number,
  deltaY: number,
): NodeImageSize => {
  const denominator = initialSize.width ** 2 + initialSize.height ** 2;
  const projectedScale =
    denominator > 0
      ? 1 + (deltaX * initialSize.width + deltaY * initialSize.height) / denominator
      : 1;
  return scaleImageSize(initialSize, projectedScale);
};

const resizeFromKeyboard = (initialSize: NodeImageSize, widthDelta: number): NodeImageSize =>
  scaleImageSize(initialSize, (initialSize.width + widthDelta) / initialSize.width);

const scaleImageSize = (initialSize: NodeImageSize, requestedScale: number): NodeImageSize => {
  const minimumScale = Math.max(
    MIN_IMAGE_SIZE_PX / initialSize.width,
    MIN_IMAGE_SIZE_PX / initialSize.height,
  );
  const maximumScale = Math.min(
    MAX_IMAGE_SIZE_PX / initialSize.width,
    MAX_IMAGE_SIZE_PX / initialSize.height,
  );
  const scale = clamp(requestedScale, minimumScale, maximumScale);
  return {
    width: clamp(Math.round(initialSize.width * scale), MIN_IMAGE_SIZE_PX, MAX_IMAGE_SIZE_PX),
    height: clamp(Math.round(initialSize.height * scale), MIN_IMAGE_SIZE_PX, MAX_IMAGE_SIZE_PX),
  };
};

const keyboardResizeDirection = (key: string): -1 | 0 | 1 => {
  if (key === 'ArrowRight' || key === 'ArrowDown') {
    return 1;
  }
  if (key === 'ArrowLeft' || key === 'ArrowUp') {
    return -1;
  }
  return 0;
};

const calculateContainedImageInsets = (
  frameSize: NodeImageSize,
  naturalSize: NodeImageSize,
): { right: number; bottom: number } => {
  if (
    !Number.isFinite(naturalSize.width) ||
    !Number.isFinite(naturalSize.height) ||
    naturalSize.width <= 0 ||
    naturalSize.height <= 0
  ) {
    return { right: 0, bottom: 0 };
  }

  const frameRatio = frameSize.width / frameSize.height;
  const naturalRatio = naturalSize.width / naturalSize.height;
  if (naturalRatio > frameRatio) {
    const visibleHeight = frameSize.width / naturalRatio;
    return { right: 0, bottom: Math.max(0, (frameSize.height - visibleHeight) / 2) };
  }
  if (naturalRatio < frameRatio) {
    const visibleWidth = frameSize.height * naturalRatio;
    return { right: Math.max(0, (frameSize.width - visibleWidth) / 2), bottom: 0 };
  }
  return { right: 0, bottom: 0 };
};

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, value));
